
	import { Component, Vue, Emit, Watch, Prop, Ref } from "vue-property-decorator";
	import { Getter, Action, namespace } from "vuex-class";
	import { Settings } from "luxon";
	import vSelect from "vue-select";
	import { multiselectMixin } from "vue-multiselect";
    import api from "@/services/api.service";
    import { DynamicDataTypes, DynamicData, QueryDynamicData } from '@/store/tasks/types';
    import DynamicDataList from './tasks/task-types/DynamicDataList.vue';
    import { debounce } from "lodash";
    import { FilterOptionData, SelectedFilters, FilterId } from '@/store/reports/types';
    import { Select } from "@sureview/vuejs-sv-dropdown";
    import vselect3 from "vselect3";

	@Component({
		components: {
            "v-select": vSelect,
            vselect3
        }
	})
	export default class DynamicDataListSelector extends Vue {     

        @Prop({ default: null }) dynamicTypes: FilterOptionData[];
        @Prop({ default: null }) selectedFilters: SelectedFilters;

        private selectedDlls: DynamicData[] = [];

        private selectedDllType: DynamicDataTypes = null;
        private selectedDllValue: DynamicData = null;

        private dynamicDataTypes: DynamicDataTypes[] = [];
        private dynamicDataList: DynamicData[] = [];

        private atEnd: boolean = false;
        private duplicate: boolean = false;

        private async mounted() {
            // Convert types to the desired format that we have gotten from our report *init, not selected values*
            if (this.dynamicTypes) {
                const filteredDynamicTypes: any = this.dynamicTypes.map(dynamicType => ({ 
                    dataTypeId: dynamicType.id,
                    name: dynamicType.title,
                    groupId: null,
                    groupSyncId: null
                }));
                this.dynamicDataTypes = filteredDynamicTypes;
            }

            await this.fetchSelectedFilterDynamicData();
        }

        private async fetchSelectedFilterDynamicData(): Promise<void> {
            // Check if we have any selected values already.
            if (this.selectedFilters && this.selectedFilters.hasOwnProperty("dynamicdata") &&
                this.selectedFilters.dynamicdata &&
                this.selectedFilters.dynamicdata.length > 0) {

                let dynamicDataIds: number[] = [];
                this.selectedFilters.dynamicdata.forEach((ddl: FilterId) => {
                    dynamicDataIds.push(ddl.id); // Contains the ID for the value
                });

                const { data } = await api.getDynamicDataById(dynamicDataIds);
                // We want to check if we got duplicates and if not, push onto our selected filters within this component.
                data.forEach((dynamicData: DynamicData) => {
                    if (this.selectedDlls.findIndex(sdll => sdll.dynamicDataId == dynamicData.dynamicDataId) == -1) {
                        this.selectedDlls.push(dynamicData);
                    }
                });
            }
        }

        private async fetchDynamicData(taskTypeId: number = 0, filter: string = ""): Promise<void> {
            this.selectedDllValue = null;
            this.dynamicDataList = [];
            // Query to get the data for this type.
            const { data } = await api.queryDynamicDataByIdAndFilter(taskTypeId, filter);
            if (data.dynamicData && data.dynamicData.length > 0) {
                this.dynamicDataList = data.dynamicData;
                this.atEnd = data.atEnd;
            }
        }

        private deleteSelectedDdl(dynamicDataId: number): void {
            const index = this.selectedDlls.findIndex(ddl => ddl.dynamicDataId == dynamicDataId);
            if (index >= 0) {
                this.selectedDlls.splice(index, 1);
            }
            // Lets also remove this from our selected filters if it exists
            this.$emit("input", this.selectedDlls.length > 0  ? this.selectedDlls.map(ddls => ({ id: ddls.dynamicDataId })) : []);
        }

        private addToSelectedDdls(): void {
            if (!this.selectedDllType || !this.selectedDllValue) return;
            const verifyDuplicate = this.selectedDlls.findIndex(
                x => x.dataTypeId == this.selectedDllType.dataTypeId
                && x.dynamicDataId == this.selectedDllValue.dynamicDataId);

            if (verifyDuplicate >= 0) {
                this.duplicate = true;

                this.selectedDllType = null;
                this.selectedDllValue = null;
                this.dynamicDataList = [];
                setTimeout(() => {
                    this.duplicate = false;
                }, 2500);
                return;
            }
           
            this.selectedDlls.push(this.selectedDllValue);
            this.$emit("input", this.selectedDlls.map(ddls => { return  { id: ddls.dynamicDataId } }));
    
            this.selectedDllType = null;
            this.selectedDllValue = null;
            this.dynamicDataList = [];
        }

        private async addTypeToDdl(): Promise<void> {
            if (!this.selectedDllType) {
                this.selectedDllValue = null;
                return;
            }

            await this.fetchDynamicData(this.selectedDllType.dataTypeId, "");
        }

        private addValueToDdl(): void {
            if (!this.selectedDllValue) return;
            this.addToSelectedDdls();
        }

        /** Retrieves the name of a dynamic data type by using dataTypeId */
        private getTypeById(dataTypeId: number): string {
            const dType = this.dynamicDataTypes.find((type: DynamicDataTypes) => type.dataTypeId == dataTypeId);
            return dType ? dType.name : "Type not found";
        }

        private debounceDynamicDataFilter = debounce(async (filter: string, loading: any) => {
            await this.queryDynamicData(filter);
        }, 300);

        private async queryDynamicData(filter: string): Promise<void> {
            if (!this.selectedDllType) {
                return;
            }
            await this.fetchDynamicData(this.selectedDllType.dataTypeId, filter);
        }
	}
